// TraceSearchPage — Ricerca di tracciabilità (T-138, READ-ONLY, offline-first).
//
// Caso d'uso: ispettore HACCP / titolare che parte da un LOTTO o da un ALIMENTO
// e vuole risalire a cascata a tutto lo storico collegato:
//   - per un LOTTO   → il carico (stock_in) col fornitore + DDT/fattura di origine,
//                       le produzioni che l'hanno consumato, le etichette stampate.
//   - per un ALIMENTO → tutti i suoi lotti storici (caricati / usati / etichettati)
//                       coi rispettivi documenti.
//
// È SOLO LETTURA: nessuna mutation, nessuna chiamata di rete. Legge esclusivamente
// dai dati locali via gli hook esistenti (useEvents su stock_in/stock_out/
// label_printed, useMasters su foods/suppliers/documents/productions). La logica di
// walk della catena è la stessa concettuale della trace-page edge function, ma
// ricostruita client-side sui mirror offline.

import {
  IonBackButton,
  IonBadge,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonList,
  IonNote,
  IonPage,
  IonSearchbar,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import {
  chevronDownOutline,
  chevronForwardOutline,
  cubeOutline,
  documentTextOutline,
  pricetagOutline,
  restaurantOutline,
  arrowDownOutline,
} from 'ionicons/icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  useEvents,
  useMasters,
  useFiscalDocs,
  useStockProjection,
} from '../../lib/data/queries.js';
import { convertQty } from '@trackeat/shared';
import { resolveDocSource } from '../../lib/documents/docSource.js';
import { EmptyHint, SectionHeader, palette } from '../../components/design.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import {
  selectActivePhysicalPurchaseReceipts,
  type PhysicalPurchaseEvent,
  type PhysicalPurchasePayload,
} from '../../lib/physicalPurchaseReceipts.js';
import {
  resolveTraceSearchBusinessScope,
  traceRowMatchesBusiness,
  type TraceBusinessMaster,
} from '../../lib/traceSearchScope.js';

// ───────── Tipi locali (riflettono i payload/master già in uso altrove) ─────────

interface FoodRow {
  id: string;
  name: string;
  default_uom?: string | null;
  allergens?: string[];
}

interface SupplierRow {
  id: string;
  name: string;
  piva?: string | null;
}

interface DocumentRow {
  id: string;
  document_number?: string | null;
  document_date?: string | null;
  supplier_id?: string | null;
  kind?: string | null;
}

interface BusinessRow extends TraceBusinessMaster {
  name?: string | null;
}

interface ProductionInput {
  batch_lot?: string | null;
  food_id?: string;
  food_name?: string;
  source_production_id?: string | null;
  qty_used?: number;
  uom?: string;
}

interface ProductionRow {
  id: string;
  business_id?: string | null;
  output_name: string;
  produced_at?: string | null;
  expires_at?: string | null;
  trace_token?: string | null;
  // T-147: una produzione annullata viene archiviata da cancelLabel (LabelsPage):
  // se valorizzato, l'uso "non è mai avvenuto" per il cliente e va escluso, così
  // come la sua trace-page pubblica (che filtra `archived_at IS NULL` → 404).
  archived_at?: string | null;
  inputs?: ProductionInput[];
}

type StockInEvent = PhysicalPurchaseEvent & {
  id: string;
  business_id?: string | null;
  client_ts: string;
  payload: PhysicalPurchasePayload & {
    business_id?: string | null;
    food_id: string;
    qty: number;
    uom?: string | null;
    stock_qty?: number;
    stock_uom?: string | null;
    batch_lot: string | null;
    expiration_date: string | null;
    supplier_id?: string | null;
    document_id?: string | null;
    movement_class?: string | null;
    economic_effect?: string | null;
    uom_rebase_source?: boolean;
    uom_rebase_snapshot?: boolean;
  };
};

type StockOutEvent = PhysicalPurchaseEvent & {
  id: string;
  business_id?: string | null;
  client_ts: string;
  payload: PhysicalPurchasePayload & {
    business_id?: string | null;
    food_id: string;
    qty: number;
    uom?: string | null;
    stock_qty?: number;
    stock_uom?: string | null;
    batch_lot: string | null;
    reason?: string | null;
    movement_class?: string | null;
    economic_effect?: string | null;
    uom_rebase_source?: boolean;
    uom_rebase_snapshot?: boolean;
  };
};

interface LabelPrintedEvent {
  id: string;
  client_ts: string;
  payload: {
    lp_code?: string | null;
    preset?: string | null;
    produced_at?: string | null;
    expires_at?: string | null;
    copies?: number | null;
    data?: {
      product?: string | null;
      production_id?: string | null;
      trace_token?: string | null;
      is_reprint?: boolean | null;
    } | null;
  };
}

// T-147: evento append-only emesso da cancelLabel (LabelsPage) quando un'etichetta
// viene annullata. `original_event_id` punta all'id della label_printed annullata.
// Lo storico etichette già filtra su questo; la ricerca tracciabilità deve fare
// lo stesso, altrimenti "Apri tracciabilità" punta a un trace_token la cui
// produzione è stata archiviata → la trace-page risponde "Lavorazione non trovata".
interface LabelVoidedEvent {
  id: string;
  client_ts: string;
  payload: {
    original_event_id?: string | null;
    reason?: string | null;
    voided_at?: string | null;
  };
}

// ───────── Modello aggregato dell'esito di ricerca ─────────

interface LotLoad {
  eventId: string;
  qty: number;
  uom: string;
  expiration: string | null;
  loadedAt: string;
  supplierName: string | null;
  supplierPiva: string | null;
  documentId: string | null;
  documentNumber: string | null;
  documentDate: string | null;
  documentType: string | null; // 'DDT' | 'Fattura' | … (per dare valore al link)
}

interface LotConsumption {
  productionId: string;
  productionName: string;
  producedAt: string | null;
  qtyUsed: number | null;
  uom: string | null;
  traceToken: string | null;
}

interface LotLabel {
  eventId: string;
  lpCode: string | null;
  product: string | null;
  printedAt: string;
  copies: number | null;
  traceToken: string | null;
  productionId: string | null;
}

// Una "scheda lotto" = la riga risultato espandibile principale.
interface LotResult {
  key: string; // foodId|lot
  foodId: string;
  foodName: string;
  lot: string | null;
  totalIn: number;
  totalOut: number;
  remaining: number;
  uom: string;
  loads: LotLoad[];
  consumptions: LotConsumption[];
  labels: LotLabel[];
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' });
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtQty(n: number, uom: string): string {
  const rounded = Math.round(n * 1000) / 1000;
  return `${rounded} ${uom}`.trim();
}

function normalizedLot(lot: string | null | undefined): string | null {
  const clean = lot?.trim() ?? '';
  return clean || null;
}

function lotResultKey(foodId: string, lot: string | null | undefined): string {
  return `${foodId}|${normalizedLot(lot) ?? ''}`;
}

function isUomRebasePayload(payload: {
  movement_class?: string | null;
  uom_rebase_source?: boolean;
  uom_rebase_snapshot?: boolean;
}): boolean {
  return payload.movement_class === 'uom_rebase'
    || payload.uom_rebase_source === true
    || payload.uom_rebase_snapshot === true;
}

/** Etichetta leggibile del tipo documento fiscale (per il link "Apri documento"). */
function docTypeLabel(t: string | null | undefined): string {
  switch (t) {
    case 'fattura': return 'Fattura';
    case 'nota_credito': return 'Nota di credito';
    case 'nota_debito': return 'Nota di debito';
    case 'autofattura': return 'Autofattura';
    case 'ddt': return 'DDT';
    default: return 'Documento';
  }
}

export function TraceSearchPage(): JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { profile } = useAuth();
  const activeTenantId = profile?.active_tenant_id ?? null;
  const activeBusinessId = profile?.active_business_id ?? null;

  const { data: foods = [] } = useMasters<FoodRow>('foods');
  const {
    data: businesses = [],
    isLoading: businessesLoading,
    isError: businessesError,
  } = useMasters<BusinessRow>('businesses');
  const { data: suppliers = [] } = useMasters<SupplierRow>('suppliers');
  const { data: documents = [] } = useMasters<DocumentRow>('documents');
  // T-195: le FATTURE non sono nel master locale `documents` (solo DDT). Le
  // carichiamo a parte (tenant attivo, cassetti separati) per mostrare numero/
  // fornitore dei carichi che vengono da una fattura.
  const { data: fiscalDocs = [] } = useFiscalDocs();
  const { data: productions = [] } = useMasters<ProductionRow>('productions');
  // limit=null: storico completo del tenant — questa pagina è la "fonte di verità"
  // per un'ispezione, un cap silenzioso falsificherebbe la catena.
  const { data: stockIn = [] } = useEvents('stock_in', null);
  const { data: stockOut = [] } = useEvents('stock_out', null);
  const traceBusinessScope = useMemo(
    () => resolveTraceSearchBusinessScope({
      businesses,
      tenantId: activeTenantId,
      activeBusinessId,
      businessesLoading,
      businessesError,
    }),
    [
      activeBusinessId,
      activeTenantId,
      businesses,
      businessesError,
      businessesLoading,
    ],
  );
  const scopedStockIn = useMemo(
    () => (stockIn as unknown as StockInEvent[]).filter((event) =>
      traceRowMatchesBusiness(event, traceBusinessScope)
    ),
    [stockIn, traceBusinessScope],
  );
  const scopedStockOut = useMemo(
    () => (stockOut as unknown as StockOutEvent[]).filter((event) =>
      traceRowMatchesBusiness(event, traceBusinessScope)
    ),
    [stockOut, traceBusinessScope],
  );
  const purchaseStockIn = useMemo(
    () => selectActivePhysicalPurchaseReceipts(
      scopedStockIn,
      scopedStockOut as PhysicalPurchaseEvent[],
    ),
    [scopedStockIn, scopedStockOut],
  );
  const canonicalUomByFood = useMemo(
    () => Object.fromEntries(
      foods
        .filter((food) => !!food.default_uom?.trim())
        .map((food) => [food.id, food.default_uom!.trim()]),
    ),
    [foods],
  );
  const { data: stockProjection } = useStockProjection({
    canonicalUomByFood,
    includeLegacyUnscopedBusiness:
      traceBusinessScope?.allowLegacyUnscoped ?? false,
  });
  const traceBalances = useMemo(
    () => traceBusinessScope ? stockProjection.balances : [],
    [stockProjection.balances, traceBusinessScope],
  );
  const scopedProductions = useMemo(
    () => (productions as ProductionRow[]).filter((production) =>
      traceRowMatchesBusiness(production, traceBusinessScope)
    ),
    [productions, traceBusinessScope],
  );
  const { data: labels = [] } = useEvents('label_printed', null);
  // T-147: etichette annullate → set degli original_event_id da escludere.
  const { data: voidedEvents = [] } = useEvents('label_voided', null);

  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const foodById = useMemo(() => new Map(foods.map((f) => [f.id, f])), [foods]);
  const supplierById = useMemo(() => new Map(suppliers.map((s) => [s.id, s])), [suppliers]);
  const docById = useMemo(() => new Map(documents.map((d) => [d.id, d])), [documents]);
  const fiscalById = useMemo(() => new Map(fiscalDocs.map((d) => [d.id, d])), [fiscalDocs]);

  // T-147: insieme degli id di label_printed annullati (esiste un label_voided
  // con original_event_id = quell'id). Per il cliente un'etichetta cancellata
  // equivale a un uso mai avvenuto, quindi qui NON va mostrata e — soprattutto —
  // "Apri tracciabilità" non deve puntare al suo trace_token (la produzione è
  // stata archiviata e la trace-page pubblica risponderebbe "Lavorazione non
  // trovata"). Lo storico di LabelsPage applica esattamente questo stesso filtro.
  const voidedLabelIds = useMemo(() => {
    const set = new Set<string>();
    for (const v of voidedEvents as LabelVoidedEvent[]) {
      const id = v.payload?.original_event_id;
      if (typeof id === 'string') set.add(id);
    }
    return set;
  }, [voidedEvents]);

  // Indice: per ogni production-id, le sue label_printed VIVE (associate via
  // payload.data.production_id). Si escludono: le etichette annullate (id in
  // voidedLabelIds) e le ristampe identiche (is_reprint) — coerente con lo
  // storico etichette, che le tiene in DB per audit ma non come righe duplicate.
  const labelsByProduction = useMemo(() => {
    const map = new Map<string, LabelPrintedEvent[]>();
    for (const ev of labels as LabelPrintedEvent[]) {
      if (voidedLabelIds.has(ev.id)) continue;
      if (ev.payload?.data?.is_reprint === true) continue;
      const pid = ev.payload?.data?.production_id;
      if (!pid) continue;
      const arr = map.get(pid) ?? [];
      arr.push(ev);
      map.set(pid, arr);
    }
    return map;
  }, [labels, voidedLabelIds]);

  // Costruzione "schede lotto": chiave foodId|lot. Una scheda raccoglie tutti i
  // carichi, i consumi (produzioni che hanno usato quel lotto) e le etichette
  // (via produzioni consumatrici).
  const lotResults: LotResult[] = useMemo(() => {
    const map = new Map<string, LotResult>();
    const projectedByKey = new Map(
      traceBalances.map((balance) => [
        lotResultKey(balance.foodId, balance.batchLot),
        balance,
      ]),
    );

    const ensure = (foodId: string, lot: string | null): LotResult => {
      const normalized = normalizedLot(lot);
      const key = lotResultKey(foodId, normalized);
      let r = map.get(key);
      if (!r) {
        const food = foodById.get(foodId);
        r = {
          key,
          foodId,
          foodName: food?.name ?? foodId.slice(0, 8),
          lot: normalized,
          totalIn: 0,
          totalOut: 0,
          remaining: 0,
          uom: food?.default_uom ?? '',
          loads: [],
          consumptions: [],
          labels: [],
        };
        map.set(key, r);
      }
      return r;
    };

    // 1) Ricezioni d'acquisto attive → carico + fornitore + documento.
    for (const ev of purchaseStockIn) {
      const p = ev.payload;
      if (!p?.food_id) continue;
      const r = ensure(p.food_id, p.batch_lot);
      const uom = p.stock_uom ?? p.uom ?? r.uom ?? '';
      if (uom && !r.uom) r.uom = uom;
      const qty = p.stock_qty ?? p.qty ?? 0;
      const canonicalQty = r.uom ? convertQty(qty, uom, r.uom) : qty;
      if (canonicalQty != null) r.totalIn += canonicalQty;
      // Il documento di carico può essere un DDT (master locale) o una FATTURA
      // (fiscalById). Numero/data/fornitore/tipo da qualunque dei due esista.
      const ddt = p.document_id ? docById.get(p.document_id) : undefined;
      const fattura = p.document_id ? fiscalById.get(p.document_id) : undefined;
      // Fornitore: dal payload, con fallback al fornitore del documento (le
      // fatture importate spesso hanno il fornitore solo sul documento).
      const supplierId = p.supplier_id ?? ddt?.supplier_id ?? fattura?.supplier_id ?? null;
      const supplier = supplierId ? supplierById.get(supplierId) : undefined;
      r.loads.push({
        eventId: ev.id,
        qty,
        uom: uom || r.uom,
        expiration: p.expiration_date,
        loadedAt: ev.client_ts,
        supplierName: supplier?.name ?? null,
        supplierPiva: supplier?.piva ?? null,
        documentId: p.document_id ?? null,
        documentNumber: ddt?.document_number ?? fattura?.number ?? null,
        documentDate: ddt?.document_date ?? fattura?.date ?? null,
        documentType: ddt ? 'DDT' : fattura ? docTypeLabel(fattura.type) : null,
      });
    }

    // 2) Scarichi (stock_out) → solo per il bilancio giacenza.
    for (const ev of scopedStockOut) {
      const p = ev.payload;
      if (!p?.food_id) continue;
      if (isUomRebasePayload(p)) continue;
      const r = ensure(p.food_id, p.batch_lot);
      const qty = p.stock_qty ?? p.qty ?? 0;
      const uom = p.stock_uom ?? p.uom ?? r.uom;
      const canonicalQty = r.uom ? convertQty(qty, uom ?? '', r.uom) : qty;
      if (canonicalQty != null) r.totalOut += canonicalQty;
    }

    // 3) Produzioni che hanno consumato un lotto → consumption + etichette collegate.
    for (const prod of scopedProductions) {
      // T-147: una produzione archiviata = etichetta annullata da cancelLabel
      // (che setta archived_at + emette stock_in compensatori). Per il cliente
      // l'uso non è mai avvenuto: la salto del tutto (niente riga "uso", niente
      // etichetta). La giacenza resta corretta perché proviene dalla proiezione
      // canonica, che applica anche il comando compensativo senza mostrarlo
      // come un nuovo acquisto.
      if (prod.archived_at) continue;
      for (const inp of prod.inputs ?? []) {
        if (!inp.food_id) continue;
        // I lotti reali hanno batch_lot; gli input da semilavorato
        // (source_production_id) non sono lotti di magazzino e li saltiamo qui.
        if (inp.source_production_id) continue;
        const r = ensure(inp.food_id, inp.batch_lot ?? null);
        r.consumptions.push({
          productionId: prod.id,
          productionName: prod.output_name,
          producedAt: prod.produced_at ?? null,
          qtyUsed: inp.qty_used ?? null,
          uom: inp.uom ?? null,
          traceToken: prod.trace_token ?? null,
        });
        const evs = labelsByProduction.get(prod.id) ?? [];
        for (const lev of evs) {
          r.labels.push({
            eventId: lev.id,
            lpCode: lev.payload?.lp_code ?? null,
            product: lev.payload?.data?.product ?? null,
            printedAt: lev.payload?.produced_at ?? lev.client_ts,
            copies: lev.payload?.copies ?? null,
            traceToken: lev.payload?.data?.trace_token ?? prod.trace_token ?? null,
            productionId: prod.id,
          });
        }
      }
    }

    for (const balance of traceBalances) {
      ensure(balance.foodId, balance.batchLot);
    }
    for (const r of map.values()) {
      const balance = projectedByKey.get(r.key);
      if (balance) {
        r.uom = balance.uom;
        r.remaining = Math.round(balance.availableQty * 1000) / 1000;
      } else {
        // Evento fuori sede o coordinata in quarantine: non inventare un saldo
        // sottraendo quantità grezze dimensionalmente incompatibili.
        r.remaining = 0;
      }
    }
    return [...map.values()];
  }, [
    purchaseStockIn,
    scopedStockOut,
    scopedProductions,
    foodById,
    supplierById,
    docById,
    fiscalById,
    labelsByProduction,
    traceBalances,
  ]);

  // Filtraggio sulla query: match per lotto OPPURE per nome alimento.
  const trimmed = query.trim().toLowerCase();
  const filtered: LotResult[] = useMemo(() => {
    if (trimmed.length < 1) return [];
    return lotResults
      .filter((r) => {
        const lotMatch = (r.lot ?? '').toLowerCase().includes(trimmed);
        const foodMatch = r.foodName.toLowerCase().includes(trimmed);
        return lotMatch || foodMatch;
      })
      .sort((a, b) => {
        // Prima i match esatti di lotto, poi per data carico più recente.
        const aLot = (a.lot ?? '').toLowerCase() === trimmed ? 0 : 1;
        const bLot = (b.lot ?? '').toLowerCase() === trimmed ? 0 : 1;
        if (aLot !== bLot) return aLot - bLot;
        const aDate = a.loads[0]?.loadedAt ?? '';
        const bDate = b.loads[0]?.loadedAt ?? '';
        return bDate.localeCompare(aDate);
      });
  }, [lotResults, trimmed]);

  function toggle(key: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function openProduction(traceToken: string | null): void {
    if (!traceToken) return;
    history.push(`/warehouse/trace/${traceToken}`);
  }

  function openDocument(documentId: string | null): void {
    if (!documentId) return;
    // T-195: il document_id può essere un DDT (public.documents, nel master
    // locale) o una FATTURA (fiscal_archive, fuori master). Instradiamo alla
    // sorgente corretta — hardcodare 'public' faceva fallire le fatture con
    // "Documento non trovato" (la maggior parte dei carichi reali sono fatture).
    const source = resolveDocSource(documentId, docById);
    history.push(`/documents/${source}/${documentId}`);
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/today" />
          </IonButtons>
          <IonTitle>{t('trace.search.title')}</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSearchbar
            value={query}
            debounce={150}
            placeholder={t('trace.search.placeholder')}
            onIonInput={(e) => setQuery(e.detail.value ?? '')}
          />
        </IonToolbar>
      </IonHeader>

      <IonContent style={{ '--background': palette.bg } as React.CSSProperties}>
        <div style={{ padding: '12px 16px 24px' }}>
          {trimmed.length < 1 ? (
            <div
              style={{
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: 12,
                padding: '12px 14px',
                fontSize: 13,
                color: '#1e40af',
                lineHeight: 1.5,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                🔎 {t('trace.search.howTitle')}
              </div>
              <div>{t('trace.search.howBody')}</div>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ background: palette.card, padding: 16, borderRadius: 14 }}>
              <EmptyHint>{t('trace.search.noResults')}</EmptyHint>
            </div>
          ) : (
            <>
              <SectionHeader
                title={t('trace.search.resultsCount', { count: filtered.length })}
              />
              <IonList style={{ background: 'transparent', padding: 0 }} lines="none">
                {filtered.map((r) => {
                  const isOpen = expanded.has(r.key);
                  return (
                    <div
                      key={r.key}
                      style={{
                        background: palette.card,
                        borderRadius: 14,
                        margin: '8px 0',
                        boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Intestazione scheda lotto — tap per espandere */}
                      <button
                        type="button"
                        onClick={() => toggle(r.key)}
                        style={{
                          all: 'unset',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          width: '100%',
                          boxSizing: 'border-box',
                          padding: '14px',
                          cursor: 'pointer',
                        }}
                        aria-expanded={isOpen}
                      >
                        <IonIcon
                          icon={pricetagOutline}
                          style={{ fontSize: 20, color: palette.accent, flexShrink: 0 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: palette.text }}>
                            {r.foodName}
                          </div>
                          <div style={{ fontSize: 12, color: palette.textFaint, marginTop: 2 }}>
                            {r.lot
                              ? `${t('trace.search.lot')} ${r.lot}`
                              : t('trace.search.noLot')}
                            {' · '}
                            {t('trace.search.remaining')}: {fmtQty(r.remaining, r.uom)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          {r.loads.length > 0 && (
                            <IonBadge color="primary" style={{ fontSize: 10 }}>
                              {r.loads.length} {t('trace.search.loadsShort')}
                            </IonBadge>
                          )}
                          {r.consumptions.length > 0 && (
                            <IonBadge color="success" style={{ fontSize: 10 }}>
                              {r.consumptions.length} {t('trace.search.usesShort')}
                            </IonBadge>
                          )}
                          {r.labels.length > 0 && (
                            <IonBadge color="warning" style={{ fontSize: 10 }}>
                              {r.labels.length} {t('trace.search.labelsShort')}
                            </IonBadge>
                          )}
                        </div>
                        <IonIcon
                          icon={isOpen ? chevronDownOutline : chevronForwardOutline}
                          style={{ fontSize: 18, color: palette.textFaint, flexShrink: 0 }}
                        />
                      </button>

                      {isOpen && (
                        <div style={{ padding: '0 14px 12px' }}>
                          {/* ── Sezione CARICO (stock_in + fornitore + documento) ── */}
                          <SubHeader
                            icon={cubeOutline}
                            color={palette.accent}
                            label={t('trace.search.loadsTitle')}
                          />
                          {r.loads.length === 0 ? (
                            <Faint>{t('trace.search.noLoads')}</Faint>
                          ) : (
                            r.loads.map((l) => (
                              <div key={l.eventId} style={rowStyle}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: palette.text }}>
                                  {fmtQty(l.qty, l.uom)} · {fmtDateTime(l.loadedAt)}
                                </div>
                                <div style={{ fontSize: 13, color: palette.text, marginTop: 2 }}>
                                  {t('trace.search.supplier')}:{' '}
                                  <b>{l.supplierName ?? t('trace.search.unknownSupplier')}</b>
                                </div>
                                {l.expiration && (
                                  <div style={{ fontSize: 12, color: palette.warn, marginTop: 2 }}>
                                    {t('trace.search.expires')}: {fmtDate(l.expiration)}
                                  </div>
                                )}
                                {l.documentId ? (
                                  <button
                                    type="button"
                                    onClick={() => openDocument(l.documentId)}
                                    style={linkBtnStyle}
                                  >
                                    <IonIcon
                                      icon={documentTextOutline}
                                      style={{ verticalAlign: -2, marginRight: 4 }}
                                    />
                                    {t('trace.search.openDoc')}
                                    {l.documentType || l.documentNumber
                                      ? ` — ${l.documentType ?? 'Documento'}${
                                          l.documentNumber ? ` n.${l.documentNumber}` : ''
                                        }${l.documentDate ? ` · ${fmtDate(l.documentDate)}` : ''}`
                                      : ''}
                                  </button>
                                ) : (
                                  <Faint>{t('trace.search.noDoc')}</Faint>
                                )}
                              </div>
                            ))
                          )}

                          {/* ── Sezione PRODUZIONI che hanno consumato il lotto ── */}
                          <SubHeader
                            icon={restaurantOutline}
                            color={palette.success}
                            label={t('trace.search.usesTitle')}
                          />
                          {r.consumptions.length === 0 ? (
                            <Faint>{t('trace.search.noUses')}</Faint>
                          ) : (
                            r.consumptions.map((c, i) => (
                              <button
                                key={`${c.productionId}-${i}`}
                                type="button"
                                onClick={() => openProduction(c.traceToken)}
                                disabled={!c.traceToken}
                                style={{
                                  ...rowStyle,
                                  ...(c.traceToken ? clickableRowExtra : {}),
                                  display: 'block',
                                  width: '100%',
                                  textAlign: 'left',
                                  border: 'none',
                                  background: 'transparent',
                                }}
                              >
                                <div style={{ fontSize: 13, fontWeight: 600, color: palette.text }}>
                                  {c.productionName}
                                </div>
                                <div style={{ fontSize: 12, color: palette.textMuted, marginTop: 2 }}>
                                  {c.producedAt ? fmtDateTime(c.producedAt) : '—'}
                                  {c.qtyUsed != null
                                    ? ` · ${fmtQty(c.qtyUsed, c.uom ?? r.uom)} ${t('trace.search.used')}`
                                    : ''}
                                </div>
                                {c.traceToken && (
                                  <div style={{ fontSize: 11, color: palette.accent, marginTop: 2 }}>
                                    {t('trace.search.openTrace')} →
                                  </div>
                                )}
                              </button>
                            ))
                          )}

                          {/* ── Sezione ETICHETTE stampate ── */}
                          <SubHeader
                            icon={pricetagOutline}
                            color={palette.warn}
                            label={t('trace.search.labelsTitle')}
                          />
                          {r.labels.length === 0 ? (
                            <Faint>{t('trace.search.noLabels')}</Faint>
                          ) : (
                            r.labels.map((lb) => (
                              <button
                                key={lb.eventId}
                                type="button"
                                onClick={() => openProduction(lb.traceToken)}
                                disabled={!lb.traceToken}
                                style={{
                                  ...rowStyle,
                                  ...(lb.traceToken ? clickableRowExtra : {}),
                                  display: 'block',
                                  width: '100%',
                                  textAlign: 'left',
                                  border: 'none',
                                  background: 'transparent',
                                }}
                              >
                                <div style={{ fontSize: 13, fontWeight: 600, color: palette.text }}>
                                  {lb.product ?? '—'}
                                  {lb.lpCode ? ` · ${lb.lpCode}` : ''}
                                </div>
                                <div style={{ fontSize: 12, color: palette.textMuted, marginTop: 2 }}>
                                  {fmtDateTime(lb.printedAt)}
                                  {lb.copies != null ? ` · ${lb.copies}× ${t('trace.search.copies')}` : ''}
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </IonList>
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}

// ───────── piccoli componenti di presentazione locali ─────────

const rowStyle: React.CSSProperties = {
  padding: '8px 10px',
  marginTop: 6,
  borderRadius: 10,
  background: '#f8fafc',
  boxSizing: 'border-box',
};

const clickableRowExtra: React.CSSProperties = {
  cursor: 'pointer',
};

const linkBtnStyle: React.CSSProperties = {
  all: 'unset',
  display: 'inline-block',
  marginTop: 6,
  fontSize: 12,
  fontWeight: 600,
  color: palette.accent,
  cursor: 'pointer',
};

function SubHeader(props: { icon: string; color: string; label: string }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginTop: 14,
        marginBottom: 2,
        fontSize: 12,
        fontWeight: 700,
        color: props.color,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
      }}
    >
      <IonIcon icon={arrowDownOutline} style={{ fontSize: 12, opacity: 0.7 }} />
      <IonIcon icon={props.icon} style={{ fontSize: 14 }} />
      {props.label}
    </div>
  );
}

function Faint(props: { children: React.ReactNode }): JSX.Element {
  return (
    <IonItem lines="none" style={{ '--background': 'transparent', '--min-height': '0' } as React.CSSProperties}>
      <IonNote style={{ fontSize: 12, color: palette.textFaint, paddingLeft: 0 }}>
        {props.children}
      </IonNote>
    </IonItem>
  );
}
